import '@fortawesome/fontawesome-free/css/all.min.css'
import { LiveVideoRoomParticipantTrack, LiveVideoRoomParticipantTracks } from '@irontrainers/live-assist/lib/rtdb'
import React from 'react'
import { Button, Card, Col, Dropdown, Form, Row } from 'react-bootstrap'
import ironFit, { liveSupport, staffs } from '../../../clients/iron-fit'
import { Timer } from '../../../components'
import { PatientNameWithIcon } from '../../../components/Patient_Name'
import { StaffNameWithIcon } from '../../../components/Staff_Name'
import { QueueCardProps } from '../types'
import './QueueCard.scss'
import Swal from 'sweetalert2'
import { ReturningStaffAssignment } from './ReturningStaffAssignment'

declare var screen: any

function getPopupSettings() {
  const width = screen.availWidth
  const height = screen.availHeight
  return `scrollbars=yes,resizable=yes,status=no,location=no,toolbar=no,menubar=no,width=${width},height=${height}`
}

const QueueCard: React.FC<QueueCardProps> = ({
  userId,
  userType,
  quitSince,
  awaitingSince,
  queuePriority,
  returningSupportId,
  supportStartTime,
  supportEndTime,
  supportId,
  indexNum: queueNumber,
  hasByPass,
  preferredStaffDepartment,
  onlineDevices = 0,
  preferredStaffId
}) => {
  const priority = (queuePriority ?? 0) > 0 || false
  const returnVisit = !!returningSupportId
  const [support] = liveSupport.useLiveSupport(userType !== 'patient' ? supportId : undefined)
  const [staffDetails] = staffs.useStaff(userId)
  const supportingPatientId = support && support.patientId && !support.patientReviewTime ? support.patientId : undefined
  const [supportingPatient] = liveSupport.useLivePatient(supportingPatientId)
  const [videoRoom] = liveSupport.useVideoRoom(supportId)
  const videoRoomSupportingParticipant =
    videoRoom && videoRoom.participants && supportingPatientId ? videoRoom.participants[supportingPatientId] : undefined
  const videoRoomSupportingParticipantTracks = videoRoomSupportingParticipant ? videoRoomSupportingParticipant.tracks : undefined
  const videoRoomCurrentParticipant = videoRoom && videoRoom.participants ? videoRoom.participants[userId] : undefined
  const videoRoomCurrentParticipantTracks = videoRoomCurrentParticipant ? videoRoomCurrentParticipant.tracks : undefined
  const supportingPatientMediaStatus = useVideoRoomMediaState(videoRoomSupportingParticipantTracks) // patient audio video tracks
  const mediaStatus = useVideoRoomMediaState(videoRoomCurrentParticipantTracks) // staff audio video tracks
  const [isPriority, setIsPriority] = React.useState<boolean>(priority)
  const [isPreviousStaffAssign, setIsPreviousStaffAssign] = React.useState<boolean>(false)
  const isTransferStaffBtnVisible = support && ['operator', 'doctor', 'psychologist', 'assistant'].includes(userType)
  const supportingPatientOnlineClassName = onlineDevices >= 1 ? '' : 'text-danger'
  const [preferredStaff] = staffs.useStaff(preferredStaffId ? preferredStaffId : undefined)

  const api = ironFit._clients().api

  const fallbackSupportStartTime = supportStartTime || (support && support.startTime)
  const fallbackSupportEndTime = supportEndTime || (support && support.endTime)

  function useVideoRoomMediaState(tracks?: LiveVideoRoomParticipantTracks) {
    return React.useMemo(() => {
      if (!tracks) return {}

      let audioTrack: LiveVideoRoomParticipantTrack | undefined = undefined
      let videoTrack: LiveVideoRoomParticipantTrack | undefined = undefined
      for (const trackId in tracks) {
        const track = tracks[trackId]
        if (track.kind === 'video') videoTrack = track
        if (track.kind === 'audio') audioTrack = track
      }

      type TrackStatus = 'offline' | 'enabled' | 'disabled'
      const microphone: TrackStatus = audioTrack ? (audioTrack.enabled ? 'enabled' : 'disabled') : 'offline'
      const video: TrackStatus = videoTrack ? (videoTrack.enabled ? 'enabled' : 'disabled') : 'offline'

      return { microphone, video }
    }, [tracks])
  }

  const statusConfig = (() => {
    if ((userType === 'patient' && returnVisit) || (supportId && support?.returningSupportId)) {
      return {
        label: 'Retorno',
        // label: 'Return',
        badgeClass: 'badge-return',
        showIcon: false,
      }
    }

    if (awaitingSince) {
      return {
        // label: 'Waiting',
        label: 'Em Espera',
        badgeClass: 'badge-waiting',
        showIcon: true,
      }
    }

    if (quitSince) {
      return {
        label: 'Vídeo desligado', // video off
        badgeClass: 'badge-danger',
        showIcon: false,
      }
    }

    if (supportId && fallbackSupportEndTime) {
      return {
        label: 'Em análise',
        // label: 'In Review',
        badgeClass: 'badge-warning',
        showIcon: false,
      }
    }

    if (supportId) {
      return {
        // label: 'In Support',
        label: 'Em Atendimento',
        badgeClass: 'badge-attending',
        showIcon: false,
      }
    }

    return {
      label: 'Unknown',
      badgeClass: 'badge-default',
      showIcon: false,
    }
  })()

  const finalizeCall = async () => {
    if (userType === 'patient' && !supportingPatientId) {
      removePatientOption(false, userId, supportId)
    }
    else if (supportId && support) {
      removeStaffAndPatientFromCall(support, supportId)
    }
    else {
      removeStaffOption(userType, userId)
    }
  }

  const removePatientOption = React.useCallback(async (isInSupport: Boolean, userDetail: any, supportId?: string) => {
    Swal.fire({
      title: 'você quer manter o paciente?',
      showDenyButton: true,
      confirmButtonText: 'Derrubar paciente',
      denyButtonText: `Cancelar`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        await kickOutPatient(isInSupport, userDetail, supportId)
        Swal.fire('Derrubar paciente', '', 'success')
      }
    })
  }, [])


  const kickOutStaffAndPAtientFromCall = async (userType: string, staffId: string,
    runningSupportId: string, patientId: string) => {
    try {
      const reqParam = { userType, staffId, runningSupportId, patientId }
      const res = await api.post('/live-support/removeStaffAndPatientFromPA', reqParam)
      return res.data
    } catch (e) {
      throw e
    }
  }


  const removeStaffAndPatientFromCall = React.useCallback(async (support: any, supportId: string) => {
    Swal.fire({
      title: 'Você quer encerrar a chamada?',
      showDenyButton: true,
      confirmButtonText: 'Sim',
      denyButtonText: `Não`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        const res = await kickOutStaffAndPAtientFromCall(support.staffType, support.staffId,
          supportId, support.patientId)
        if (res.message === 'success!') {
          Swal.fire('Corte de chamada realizado com sucesso', '', 'success')
        } else {
          Swal.fire('Corte de chamada não realizado', '', 'info')
        }
      } else {
        Swal.fire('Corte de chamada não realizado', '', 'info')
      }
    })
  }, [support])

  const removeStaffOption = React.useCallback(
    async (userType: string, userId: string) => {
      Swal.fire({
        title: 'você quer manter a pessoal?',
        showDenyButton: true,
        confirmButtonText: 'Derrubar pessoal',
        denyButtonText: `Cancelar`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const res = await kickOutStaff(userType, userId)
          if (res.message === 'success!') {
            Swal.fire('Derrubar pessoal', '', 'success')
          } else {
            Swal.fire('Staff not removed', '', 'info')
          }
        }
      })
    },
    [userType, userId]
  )

  const kickOutPatient = async (isInSupport: Boolean, userDetail: string, supportId?: string) => {
    try {
      let reqParam = {}
      if (isInSupport) {
        reqParam = {
          patientId: userDetail,
          supportId,
          kickoutFrom: 'monitor',
        }
      } else {
        reqParam = {
          patientId: userDetail,
        }
      }
      const res = await api.post('/live-support/removePatientFromPA', reqParam)
      return res.data
    } catch (e) {
      throw e
    }
  }

  const kickOutStaff = async (userType: string, staffId: string) => {
    try {
      const reqParam = { userType, staffId }
      const res = await api.post('/live-support/removeStaffFromPA', reqParam)
      return res.data
    } catch (e) {
      throw e
    }
  }

  const handleByPass = async () => {
    try {
      const res = await api.post('/live-support/bypass', {
        transferToDoctor: true,
        transferWithPriority: 1,
        patientId: userId,
      })
      return res.data
    } catch (e) {
      throw e
    }
  }

  const removePreferredStaff = async (isRemoveStaff = false) => {
    try {
      let reqParam = {}
      if (isRemoveStaff) {
        reqParam = {
          patientId: userId,
          removePreferredStaff: true
        }
      } else {
        reqParam = {
          patientId: userId,
          preferredStaffDepartment: 'Pediatrician',
        }
      }
      const res = await api.post('/live-support/removePreferredStaff', reqParam)
      return res.data
    } catch (e) {
      throw e
    }
  }

  const handleTransferPatient = async (staffType: 'pediatra' | 'doctor' | 'assistant' | 'psychologist', isPriority: boolean) => {
    try {
      const data = {
        staffType,
        transferWithPriority: isPriority ? 1 : undefined,
        patientId: userId,
      }
      setIsPriority(isPriority ? true : priority)
      const res = await api.post('/live-support/transfer-patients', data)
      return res.data
    } catch (e) {
      setIsPriority(priority)
      throw e
    }
  }

  const assignmentStaffs = React.useCallback(
    async (staffType: 'pediatra' | 'doctor' | 'assistant' | 'psychologist', isPriority: boolean) => {
      Swal.fire({
        title: 'Deseja atualizar a fila?',
        showDenyButton: true,
        confirmButtonText: 'Sim, atualizar fila',
        denyButtonText: `Cancelar`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          await handleTransferPatient(staffType, isPriority)
          Swal.fire('Sim, atualizar fila', '', 'success')
        }
      })
    },
    [isPriority]
  )

  const openSupportingPatientDebugPopup = React.useCallback(() => {
    if (!supportingPatientId) return false
    const url = new URL(window.location.href)
    url.searchParams.set('patientId', supportingPatientId)
    url.pathname = '/live-debug'
    window.open(url.href, `live-debug-${Date.now()}`, getPopupSettings())
    return false
  }, [supportingPatientId])

  const openUserDebugPopup = React.useCallback(() => {
    if (!userId) return false
    if (!userType) return false
    const url = new URL(window.location.href)
    url.searchParams.set(userType === 'patient' ? 'patientId' : 'staffId', userId)
    url.pathname = '/live-debug'
    window.open(url.href, `live-debug-${Date.now()}`, getPopupSettings())
    return false
  }, [userId, userType])

  const handleCheckboxChange = (e: any) => {
    if (priority) return
    setIsPriority(!isPriority)
  }

  const assignPreviousConsultation = () => {
    setIsPreviousStaffAssign(true)
  }

  return (
    <Card className={`queue-card ${statusConfig.badgeClass} mb-3 border-0 w-100`}>
      <Card.Body className="p-3 d-flex flex-column">
        <div className="top-info">
          <Row className="status-row align-items-center mb-2">
            <Col xs={7} className="d-flex align-items-center">
              {priority && <i className="fas fa-exclamation-triangle status-icon mr-1" />}

              <span className={`status-badge ${statusConfig.badgeClass}`}>{statusConfig.label}</span>
            </Col>

            <Col xs={5} className="wait-time d-flex justify-content-end align-items-center">
              <i className={`fa-solid ${userType !== 'patient' ? 'fa-headphones' : 'fa-clock'}`} />
              {quitSince && <Timer startTime={quitSince} />}
              {awaitingSince && <Timer startTime={awaitingSince} />}
              {supportId && fallbackSupportStartTime && !fallbackSupportEndTime && <Timer startTime={fallbackSupportStartTime} />}
              {supportId && fallbackSupportStartTime && fallbackSupportEndTime && <Timer startTime={fallbackSupportEndTime} />}
            </Col>
          </Row>

          <div className="main-name d-flex align-items-center mb-1">
            <h5 className="mb-0">
              {userType === 'patient' ? (
                <>
                  <a
                    className="h6 text-uppercase text-truncate d-inline-block"
                    style={{ maxWidth: 260 }}
                    target="popup"
                    onClick={openUserDebugPopup}
                  >
                    <PatientNameWithIcon
                      patientId={userId}
                      videoStatus={supportingPatientMediaStatus.video}
                      microphoneStatus={supportingPatientMediaStatus.microphone}
                      supportingPatientOnlineClassName={supportingPatientOnlineClassName}
                    />
                  </a>
                  {preferredStaffDepartment && (
                    <div className="d-flex align-items-center text-muted mt-2">
                      <small className="text-truncate font-weight-600">
                        Department: {preferredStaffDepartment}{' '}
                        <img
                          src="/img/icons/cards/cancel.png"
                          style={{ height: '10%', width: '10%', marginLeft: '15px', cursor: 'pointer' }}
                          alt="Remove Preferred Staff"
                          title="Remove Preferred Staff"
                          onClick={() => removePreferredStaff(false)}
                        />
                      </small>
                    </div>
                  )}
                  {preferredStaffId && preferredStaff && (
                    <div className="d-flex align-items-center text-muted mt-2">
                      <small className="text-truncate font-weight-600">
                        Transferir para:
                        {preferredStaff.name}
                      </small>
                      <img
                        src="/img/icons/cards/cancel.png"
                        style={{ height: '10%', width: '10%', marginLeft: '15px', cursor: 'pointer' }}
                        alt="Remove Preferred Staff"
                        title="Remove Preferred Staff"
                        onClick={() => removePreferredStaff(true)}
                      />
                    </div>
                  )}
                </>
              ) : (
                <a
                  className="text-uppercase text-truncate d-inline-block"
                  style={{ maxWidth: 260 }}
                  target="popup"
                  onClick={openUserDebugPopup}
                >
                  <StaffNameWithIcon staffId={userId} videoStatus={mediaStatus.video} microphoneStatus={mediaStatus.microphone} />
                </a>
              )}
            </h5>
          </div>

          <div>
            {supportingPatient && supportingPatientId && (
              <>
                <a
                  className="h6 text-uppercase text-truncate d-inline-block"
                  style={{ maxWidth: 260 }}
                  target="popup"
                  onClick={openSupportingPatientDebugPopup}
                >
                  <PatientNameWithIcon
                    patientId={supportingPatientId}
                    videoStatus={supportingPatientMediaStatus.video}
                    microphoneStatus={supportingPatientMediaStatus.microphone}
                    supportingPatientOnlineClassName={supportingPatientOnlineClassName}
                  />
                </a>
                {preferredStaffDepartment && (
                  <div className="d-flex align-items-center text-muted mt-2">
                    <small className="text-truncate font-weight-600">
                      Department: {preferredStaffDepartment}{' '}
                      <img
                        src="/img/icons/cards/cancel.png"
                        style={{ height: '10%', width: '10%', marginLeft: '15px', cursor: 'pointer' }}
                        alt="Remove Preferred Staff"
                        title="Remove Preferred Staff"
                        onClick={() => removePreferredStaff(false)}
                      />
                    </small>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="actions d-flex flex-column">
          <div className="action-buttons d-flex align-items-center mb-2">
            {userType === 'patient' && queuePriority !== 1 && (
              <span className="badge bypass-badge" onClick={handleByPass}>
                By-pass
              </span>
            )}

            {(userType === 'patient' || isTransferStaffBtnVisible) && (
              <Form.Check
                type="checkbox"
                label="Prioridade"
                checked={isPriority}
                onChange={handleCheckboxChange}
                className="priority-checkbox ml-2"
              />
            )}

            {returnVisit && (
              <div className="return-visit-label d-flex align-items-center ml-2">
                <i className="fa-solid fa-rotate mr-1" />
                <span>Retorno</span>
              </div>
            )}

            {supportingPatient && supportingPatient.runningSupportId === undefined && (
              <div className="return-visit-label d-flex align-items-center ml-2">
                <img src="/img/icons/cards/patient_not_in_call.png" style={{ width: '25px' }} title="Paciente não está mais em chamada." />
              </div>
            )}
          </div>

          {(userType === 'patient' || isTransferStaffBtnVisible) && (
            <Dropdown className="action-select mb-2">
              <Dropdown.Toggle size="sm" variant="outline-secondary">
                Atribuir profissional
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => assignmentStaffs('pediatra', isPriority)}>Pediatra</Dropdown.Item>
                <Dropdown.Item onClick={() => assignmentStaffs('doctor', isPriority)}>Clínica Geral</Dropdown.Item>
                {!['assistant'].includes(userType) && (
                  <Dropdown.Item onClick={() => assignmentStaffs('assistant', isPriority)}>Assistant</Dropdown.Item>
                )}
                {!['psychologist'].includes(userType) && (
                  <Dropdown.Item onClick={() => assignmentStaffs('psychologist', isPriority)}>Psychologist</Dropdown.Item>
                )}
              </Dropdown.Menu>
            </Dropdown>
          )}

          <div className="finalize-row d-flex align-items-center">
            {supportingPatient && supportingPatient.runningSupportId === undefined
              ? (
                <></>
              ) : (
                <Button className="btn-finalize" onClick={finalizeCall}>
                  Finalizar consulta
                </Button>
              )}
            {supportingPatient && supportingPatient.runningSupportId && (
              <Button className="btn-finalize" onClick={() => removePatientOption(true, supportingPatientId, supportId)}>
                Patient Finalizar consulta
              </Button>
            )}
            {supportId && support && !supportingPatient?.runningSupportId ? (
              <Button className="btn-finalize" onClick={finalizeCall}>
                Finalizar consulta
              </Button>
            ) : null}
            {returnVisit &&
              <Button className="btn-finalize" onClick={assignPreviousConsultation}>
                Assign Previous Consulta
              </Button>}

            {queueNumber != null && (
              <span className="queue-number ml-auto d-flex gap-1">
                <i className="fa-solid fa-users" />#{queueNumber}
              </span>
            )}
          </div>
          {supportId && staffDetails?.department === 'pa-assistant' && support?.staffType !== userType && (
            <>Ligue em execução com {support?.staffType}</>
          )}
        </div>
      </Card.Body>
      {isPreviousStaffAssign && <ReturningStaffAssignment isOpen={isPreviousStaffAssign} setIsOpen={setIsPreviousStaffAssign} returningSupportId={returningSupportId || ""} />}
    </Card>
  )
}

export default QueueCard
